import React from 'react';
import { Sparkles, CreditCard, MessageSquare, Video, Image, ShoppingBag, BarChart3, Shield, DollarSign, Users, Zap, Heart, TrendingUp, Lock, Globe, Star, Play, Camera, Music, Mic, BookOpen, Award, Gift, Bell, Calendar, Layers, Send, Eye, ThumbsUp, Headphones, Palette, PenTool, Coffee, Briefcase, Map, Cpu, Wifi, Cloud, Database, FileText, Settings, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const iconMap = {
  CreditCard, MessageSquare, Video, Image, ShoppingBag, BarChart3, Shield, DollarSign, Users, Zap,
  Heart, TrendingUp, Lock, Globe, Star, Sparkles, Play, Camera, Music, Mic, BookOpen, Award,
  Gift, Bell, Calendar, Layers, Send, Eye, ThumbsUp, Headphones, Palette, PenTool, Coffee,
  Briefcase, Map, Cpu, Wifi, Cloud, Database, FileText, Settings, CheckCircle, AlertCircle, Info,
};

const resolveIcon = (name) => {
  if (!name) return CreditCard;
  if (typeof name === 'function') return name;
  return iconMap[name] || CreditCard;
};

const ITEM_ICONS = ['Users', 'MessageSquare', 'Video', 'Image', 'Gift', 'Lock'];

const EN_DEFAULTS = {
  badge_text: 'WHY JOIN',
  heading: 'Everything you want, all in one app',
  subheading: 'The easiest way to discover, connect with, and support the creators you love.',
  items: [
    { icon: 'Users', title: 'Discover Creators', description: 'Explore thousands of creators across every interest and find the ones that match your vibe.' },
    { icon: 'MessageSquare', title: 'Chat One-on-One', description: 'Send direct messages and build a real connection with the creators you support.' },
    { icon: 'Video', title: 'Live Streams', description: 'Join live broadcasts, tip in real time, and be part of the moment as it happens.' },
    { icon: 'Image', title: 'Exclusive Content', description: "Unlock photos, videos, and posts you won't find anywhere else." },
    { icon: 'Gift', title: 'Shop Custom Content', description: 'Request personalized content and shop exclusive merch directly from creators.' },
    { icon: 'Lock', title: 'Safe & Private', description: 'Your privacy comes first with secure payments and discreet billing.' },
  ],
};

const badge = 'bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08]';
const cardBg = 'bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06]';

const FeaturesBlock = () => {
  const { t } = useTranslation('auth');
  const translatedItems = t('landing.features.items', { returnObjects: true, defaultValue: [] });
  const d = {
    badge_text: t('landing.features.badge', EN_DEFAULTS.badge_text),
    heading: t('landing.features.heading', EN_DEFAULTS.heading),
    subheading: t('landing.features.subheading', EN_DEFAULTS.subheading),
    items: ITEM_ICONS.map((icon, i) => ({ icon, title: translatedItems[i]?.title, description: translatedItems[i]?.description })),
  };
  const gradients = ['from-emerald-500/20 to-teal-500/20','from-blue-500/20 to-cyan-500/20','from-red-500/20 to-pink-500/20','from-purple-500/20 to-violet-500/20','from-amber-500/20 to-orange-500/20','from-indigo-500/20 to-blue-500/20'];

  return (
    <section className="py-24 sm:py-32 relative">
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] rounded-full opacity-5 dark:opacity-8 blur-[150px] pointer-events-none"
        style={{ background: `radial-gradient(circle, var(--color-primary, #10b981) 0%, transparent 70%)` }}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="max-w-3xl mb-16 sm:mb-20">
          <div className={`inline-flex items-center gap-2 px-3.5 py-1.5 ${badge} rounded-full mb-6`}>
            <Sparkles className="w-3 h-3" style={{ color: 'var(--color-primary, #10b981)' }} />
            <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.15em]">{d.badge_text}</span>
          </div>
          <h2 className="cl-serif text-3xl sm:text-4xl lg:text-[3.25rem] font-black tracking-[-0.02em] mb-5 leading-[1.1]">{d.heading}</h2>
          <p className="text-base sm:text-lg text-gray-500 leading-relaxed font-light max-w-xl">{d.subheading}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
          {d.items.map((item, i) => {
            const Icon = resolveIcon(item.icon);
            const isWide = i === 0 || i === 5;
            return (
              <div key={i} className={`${isWide ? 'md:col-span-2' : ''} ${cardBg} rounded-[1.5rem] p-7 sm:p-8 cl-card group relative overflow-hidden shadow-sm dark:shadow-none`}>
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${gradients[i % gradients.length]} rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />
                <div className="relative">
                  <div className="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-5 h-5" style={{ color: 'var(--color-primary, #10b981)' }} />
                  </div>
                  <h3 className="cl-serif text-lg sm:text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-[14px] text-gray-500 dark:text-gray-400 leading-relaxed font-light">{item.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesBlock;
